"use client"

import { useEffect, useState } from "react"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { ActionBadge } from "@/components/ui/action-badge"
import { AddOnCard } from "@/components/ui/add-on-card"
import { ProductInfoButton } from "@/components/ui/product-info-modal"
import { ADDON_INFO } from "@/data/product-info"
import { isAvailable } from "@/lib/availability"
import { resolveServiceAvailability } from "@/lib/service-availability"
import type { CmsServiceConfig } from "@/lib/cms"
import { blobMediaUrl } from "@/lib/blob-url"
import { buildFallback, fetchPrices, type PriceData } from "@/lib/pricing"
import { useCountry } from "@/components/layout/country-provider"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { cn } from "@/lib/utils"
import { isMembershipFirstSite } from "@/lib/membership-first"

/**
 * Resolve an add-on's display price: the live Stripe full price when loaded,
 * otherwise the central fallback table in lib/pricing.ts (never a per-site
 * literal, so product prices can't drift from Stripe). `staticPrice` is only
 * for add-ons deliberately NOT in the Stripe price map (Lp(a)/PSA/Cortisol)
 * and only applies on CHF markets. Pre-launch EUR markets show "—" (their
 * fallback map is empty) until live prices arrive.
 */
export function addonDisplayPrice(
  liveFullPrice: string | undefined,
  titleKey: string,
  country: string
): string {
  if (liveFullPrice) return liveFullPrice
  return buildFallback(country).fullPrices[titleKey] ?? "—"
}

/** `membershipFirstImageSrc` is the membership-first site's picture, the
 *  booking's scan art. */
const IMAGING_ADDONS = [
  {
    titleKey: "addonBodyComposition",
    descKey: "addonBodyCompositionDesc",
    imageSrc: blobMediaUrl("/images/addons/body-composition.jpg"),
    membershipFirstImageSrc: blobMediaUrl(
      "/images/addons/scans/scan-body-composition.webp"
    ),
    slug: "body-composition",
  },
  {
    titleKey: "addonGenderHealth",
    descKey: "addonGenderHealthDesc",
    imageSrc: blobMediaUrl("/images/addons/gender-health.jpg"),
    membershipFirstImageSrc: blobMediaUrl(
      "/images/addons/scans/scan-gender-health.webp"
    ),
    slug: undefined,
  },
  {
    titleKey: "addonHeartCT",
    descKey: "addonHeartCTDesc",
    imageSrc: blobMediaUrl("/images/addons/heart-ct.jpg"),
    membershipFirstImageSrc: blobMediaUrl(
      "/images/addons/scans/scan-heart-ct.webp"
    ),
    slug: "calcium-score",
  },
  {
    titleKey: "addonMammaMRI",
    descKey: "addonMammaMRIDesc",
    imageSrc: blobMediaUrl("/images/addons/mamma-mri.jpg"),
    membershipFirstImageSrc: blobMediaUrl(
      "/images/addons/scans/scan-mamma-mri.webp"
    ),
    slug: "mamma-mri",
  },
  {
    titleKey: "addonDEXA",
    descKey: "addonDEXADesc",
    imageSrc: blobMediaUrl("/images/addons/dexa.jpg"),
    membershipFirstImageSrc: blobMediaUrl(
      "/images/addons/scans/scan-dexa.webp"
    ),
    slug: undefined,
  },
] as const

/**
 * Optional imaging add-ons grid (body composition, gender health, heart CT,
 * mamma MRI, DEXA) — shared by /pricing and the full-body-MRI page. Reads the
 * `pricing` namespace strings and live Stripe prices; gates each card by its
 * `ADDON_INFO` availability so the new-tests-pages-gated slugs stay hidden
 * where they should be. None of these add-ons carry a `cmsPackageId`, so the
 * info modal renders without biomarker groups.
 */
export function ImagingAddonsSection({
  initialPrices = null,
  servicesConfig = [],
  className,
  heading,
  excludeTitleKeys,
  footnote,
}: {
  initialPrices?: PriceData | null
  /** CMS products control plane — drives the per-market "Not available" pill. */
  servicesConfig?: CmsServiceConfig[]
  className?: string
  /** Override the default "Imaging add-ons" heading (e.g. gender-health pages
   *  show "Additional imaging add-ons"). */
  heading?: string
  /** Add-on `titleKey`s to omit (e.g. hide the Gender Health card on the
   *  gender-health pages themselves). */
  excludeTitleKeys?: string[]
  /** One-line note under the grid (e.g. the pricing page's pre-consultation
   *  line for CT, DEXA and breast MRI, AHD-13114). */
  footnote?: string
}) {
  const t = useTranslations("pricing")
  const ut = useTranslations("ui")
  const countryConfig = useCountry()
  const country = countryConfig.code
  const locale = useLocale()
  const localePath = useLocalePath()
  const [liveP, setLiveP] = useState<PriceData | null>(initialPrices)

  useEffect(() => {
    if (initialPrices) {
      setLiveP(initialPrices)
      return
    }
    fetchPrices(country).then(setLiveP)
  }, [country, initialPrices])

  const visibleImagingAddons = IMAGING_ADDONS.filter(
    (a) =>
      isAvailable(ADDON_INFO[a.titleKey]?.availability, country, locale) &&
      !excludeTitleKeys?.includes(a.titleKey)
  )
  if (visibleImagingAddons.length === 0) return null

  return (
    <section className={cn("py-16", className)}>
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <div>
          <h2 className="type-display-sm text-neutral-900">
            {heading ?? t("imagingAddonsHeading")}
          </h2>
          <p className="type-body-lg mt-3 max-w-xl text-neutral-600">
            {t("imagingAddonsDescription")}
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
          {visibleImagingAddons.map((a) => {
            // DEXA + gender-health have no pricing-card `slug` (the cards aren't
            // linked), but they still belong to the MRI category and must carry
            // the coming-soon state in a `checkup` market — gate them on their
            // test-page slug. Heart CT and mamma MRI gate on their own slugs.
            const gateSlug =
              a.titleKey === "addonDEXA"
                ? "dexa-scan"
                : a.titleKey === "addonGenderHealth"
                  ? "gender-health"
                  : a.slug
            // Imaging add-ons are MRI expansion products → category "mri", so a
            // market whose MRI is coming-soon auto-propagates to every add-on
            // (no need to mirror it per service in the CMS).
            const availability = gateSlug
              ? resolveServiceAvailability({
                  country,
                  category: "mri",
                  pathOrSlug: gateSlug,
                  servicesConfig,
                })
              : "bookable"
            // `not_available` is its own statement and keeps its own pill in
            // EVERY market, waitlist ones included (the 2026-07-29 coercion to
            // coming-soon is reverted): a scan the market cannot deliver at all
            // — mamma MRI in NL needs contrast + a radiology setup we don't
            // have — is not "coming soon", and promising it sets up a booking
            // request we have to turn down. The mixed row is the honest one.
            const notAvailable = availability === "not_available"
            const comingSoon =
              availability === "coming_soon" || availability === "waitlist"
            return (
              <div key={a.titleKey} className="relative">
                <AddOnCard
                  title={t(a.titleKey)}
                  description={t(a.descKey)}
                  price={addonDisplayPrice(
                    liveP?.fullPrices[a.titleKey],
                    a.titleKey,
                    country
                  )}
                  imageSrc={
                    isMembershipFirstSite(country)
                      ? a.membershipFirstImageSrc
                      : a.imageSrc
                  }
                  href={a.slug ? localePath(`/tests/${a.slug}`) : undefined}
                  muted={notAvailable}
                  badge={
                    notAvailable ? (
                      // Info-icon grey (neutral-100 / neutral-500): reads as
                      // inert next to the blue "coming soon" pill.
                      <ActionBadge
                        label={ut("notAvailable")}
                        color="neutral"
                        className="bg-neutral-100"
                      />
                    ) : comingSoon ? (
                      <ActionBadge label={ut("comingSoon")} type="exercise" />
                    ) : undefined
                  }
                />
                {ADDON_INFO[a.titleKey] && (
                  <div className="absolute top-3 right-3">
                    <ProductInfoButton
                      data={ADDON_INFO[a.titleKey]}
                      notAvailable={notAvailable}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {footnote && (
          <p className="type-body-md mt-6 max-w-2xl text-neutral-600">
            {footnote}
          </p>
        )}
      </div>
    </section>
  )
}
